import * as React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Button,
    Label,
    makeStyles,
    shorthands,
    mergeClasses,
    Radio,
    RadioGroup,
    Slider,
    Spinner,
    Switch,
    Text,
    tokens,
} from '@fluentui/react-components';
import { ArrowUpload20Regular, Delete20Regular, Dismiss20Regular, Save20Regular } from '@fluentui/react-icons';
import QRCode from 'qrcode';
import { IUpdatePollBrandingRequest } from '../services/DataService';
import { LOGO_POSITION_OPTIONS, LOGO_POSITION_TOP_LEFT } from '../lib/pollLabels';
import { useDataService, useAsync } from '../app/DataServiceContext';
import { useAppToast } from '../app/ToastContext';
import PageHeader from '../components/ui/PageHeader';
import Section from '../components/ui/Section';
import ErrorBar from '../components/ui/ErrorBar';
import FormFooter from '../components/ui/FormFooter';
import ColorField from '../components/ui/ColorField';
import { BlockSkeleton } from '../components/ui/Skeletons';

const BG_SOLID = 0;
const BG_GRADIENT = 1;
const MAX_LOGO_BYTES = 1_500_000;


interface IBrandingState {
    bgType: number;
    bgColor: string;
    gradientStart: string;
    gradientEnd: string;
    logoUrl: string;
    logoPosition: number;
    logoSize: number;
    qrVisible: boolean;
    qrForeground: string;
    qrBackground: string;
}

const useStyles = makeStyles({
    layout: {
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr) minmax(300px, 380px)',
        gap: '20px',
        alignItems: 'start',
        '@media (max-width: 1023px)': {
            gridTemplateColumns: 'minmax(0, 1fr)',
        },
    },
    stickyPreview: {
        position: 'sticky',
        top: '24px',
        '@media (max-width: 1023px)': {
            position: 'static',
        },
    },
    preview: {
        position: 'relative',
        aspectRatio: '16 / 10',
        minHeight: '260px',
        borderRadius: tokens.borderRadiusLarge,
        border: `1px solid ${tokens.colorNeutralStroke1}`,
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '32px',
        transitionProperty: 'background',
        transitionDuration: tokens.durationNormal,
    },
    previewText: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '8px',
        textAlign: 'center',
        maxWidth: '70%',
    },
    previewTitle: {
        fontSize: tokens.fontSizeBase500,
        fontWeight: tokens.fontWeightSemibold,
        color: '#111',
        mixBlendMode: 'luminosity',
        overflowWrap: 'anywhere',
    },
    previewLabel: {
        fontSize: tokens.fontSizeBase200,
        color: '#111',
        opacity: 0.6,
    },
    logo: {
        position: 'absolute',
        maxWidth: '45%',
        maxHeight: '40%',
        objectFit: 'contain',
    },
    qr: {
        position: 'absolute',
        right: '16px',
        bottom: '16px',
        width: '72px',
        height: '72px',
        padding: '4px',
        backgroundColor: '#fff',
        borderRadius: tokens.borderRadiusMedium,
        boxShadow: tokens.shadow8,
    },
    controls: {
        display: 'flex',
        flexDirection: 'column',
        gap: '20px',
    },
    group: {
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
    },
    upload: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        minHeight: '72px',
        padding: '12px',
        border: `1px dashed ${tokens.colorNeutralStroke1}`,
        borderRadius: tokens.borderRadiusLarge,
        color: tokens.colorNeutralForeground3,
        cursor: 'pointer',
        transitionProperty: 'border-color, color, background-color',
        transitionDuration: tokens.durationFaster,
        ':hover': {
            ...shorthands.borderColor(tokens.colorBrandStroke1),
            color: tokens.colorBrandForeground1,
            backgroundColor: tokens.colorBrandBackground2,
        },
    },
    uploadActive: {
        ...shorthands.borderColor(tokens.colorBrandStroke1),
        backgroundColor: tokens.colorBrandBackground2,
    },
    currentLogo: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
    },
    thumb: {
        height: '40px',
        maxWidth: '120px',
        objectFit: 'contain',
        padding: '2px',
        border: `1px solid ${tokens.colorNeutralStroke2}`,
        borderRadius: tokens.borderRadiusSmall,
        backgroundColor: '#fff',
    },
    positions: {
        display: 'grid',
        gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
        gap: '6px',
    },
    positionButton: {
        minHeight: '40px',
        fontSize: tokens.fontSizeBase200,
    },
    sliderHead: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'baseline',
    },
    hint: {
        fontSize: tokens.fontSizeBase200,
        color: tokens.colorNeutralForeground3,
    },
});

const LOGO_STYLE: Record<number, React.CSSProperties> = {
    0: { top: 16, left: 16 },
    1: { top: 16, left: '50%', transform: 'translateX(-50%)' },
    2: { top: 16, right: 16 },
    3: { bottom: 16, left: 16 },
    4: { bottom: 16, right: 16 },
};

const PollBrandingPage: React.FC = () => {
    const styles = useStyles();
    const navigate = useNavigate();
    const toast = useAppToast();
    const dataService = useDataService();
    const { id = '' } = useParams<{ id: string }>();
    const poll = useAsync(() => dataService.getPoll(id), [id]);

    const [state, setState] = React.useState<IBrandingState>();
    const [qrPreview, setQrPreview] = React.useState('');
    const [saving, setSaving] = React.useState(false);
    const [saveError, setSaveError] = React.useState<string>();
    const [dragOver, setDragOver] = React.useState(false);
    const fileInput = React.useRef<HTMLInputElement>(null);

    React.useEffect(() => {
        const p = poll.data;
        if (!p || state) return;
        const [gStart, gEnd] = (p.BrandingBackgroundGradient ?? '').split(',').map((s) => s.trim());
        setState({
            bgType: p.BrandingBackgroundType ?? BG_SOLID,
            bgColor: p.BrandingBackgroundColor ?? '#ffffff',
            gradientStart: gStart || '#ffffff',
            gradientEnd: gEnd || '#2a8414',
            logoUrl: p.BrandingLogoUrl ?? '',
            logoPosition: p.BrandingLogoPositionCode ?? LOGO_POSITION_TOP_LEFT,
            logoSize: p.BrandingLogoSize ?? 100,
            qrVisible: p.QrcodeVisible ?? false,
            qrForeground: p.QrcodeForeground ?? '#000000',
            qrBackground: p.QrcodeBackground ?? '#ffffff',
        });
    }, [poll.data, state]);

    const surveyUrl = dataService.getSurveyUrl(poll.data?.Slug);

    React.useEffect(() => {
        if (!state?.qrVisible || !surveyUrl) {
            setQrPreview('');
            return;
        }
        let active = true;
        QRCode.toDataURL(surveyUrl, { margin: 0, width: 160, color: { dark: state.qrForeground, light: state.qrBackground } })
            .then((result) => { if (active) setQrPreview(result); })
            .catch(() => { if (active) setQrPreview(''); });
        return () => { active = false; };
    }, [state?.qrVisible, state?.qrForeground, state?.qrBackground, surveyUrl]);

    const patch = (p: Partial<IBrandingState>): void => setState((prev) => (prev ? { ...prev, ...p } : prev));

    const readLogo = (file: File | undefined): void => {
        if (!file) return;
        if (!file.type.startsWith('image/')) {
            toast.error('Нужен файл изображения', 'PNG, JPG, SVG или WebP.');
            return;
        }
        if (file.size > MAX_LOGO_BYTES) {
            toast.error('Файл слишком большой', 'Максимум 1,5 МБ.');
            return;
        }
        const reader = new FileReader();
        reader.onload = (event) => patch({ logoUrl: String(event.target?.result ?? '') });
        reader.readAsDataURL(file);
    };

    const background = state
        ? (state.bgType === BG_GRADIENT ? `linear-gradient(135deg, ${state.gradientStart}, ${state.gradientEnd})` : state.bgColor)
        : undefined;

    const save = async (): Promise<void> => {
        if (!state || !poll.data?.Id) return;
        const request: IUpdatePollBrandingRequest = {
            BrandingBackgroundType: state.bgType,
            BrandingBackgroundColor: state.bgType === BG_SOLID ? state.bgColor : null,
            BrandingBackgroundGradient: state.bgType === BG_GRADIENT ? `${state.gradientStart}, ${state.gradientEnd}` : null,
            BrandingLogoUrl: state.logoUrl || null,
            BrandingLogoPositionCode: state.logoUrl ? state.logoPosition : null,
            BrandingLogoSize: state.logoUrl ? state.logoSize : null,
            QrcodeVisible: state.qrVisible,
            QrcodeForeground: state.qrVisible ? state.qrForeground : null,
            QrcodeBackground: state.qrVisible ? state.qrBackground : null,
        };
        try {
            setSaving(true);
            setSaveError(undefined);
            await dataService.updateBranding(poll.data.Id, request);
            toast.success('Оформление сохранено');
            navigate(`/polls/${encodeURIComponent(id)}`);
        } catch (e) {
            setSaveError((e as Error)?.message ?? 'Не удалось сохранить оформление.');
        } finally {
            setSaving(false);
        }
    };

    const detailsTo = `/polls/${encodeURIComponent(id)}`;

    return (
        <>
            <PageHeader
                backTo={{ to: detailsTo, label: 'К опросу' }}
                title="Оформление"
                subtitle={poll.data?.Title}
            />

            <ErrorBar message={poll.error} title="Не удалось загрузить опрос" onRetry={poll.reload} />
            <ErrorBar message={saveError} title="Не удалось сохранить" onDismiss={() => setSaveError(undefined)} />

            {!state && poll.loading && <Section><BlockSkeleton /></Section>}

            {state && poll.data && (
                <>
                    <div className={styles.layout}>
                        <div className={styles.stickyPreview}>
                            <Section title="Предпросмотр экрана участника">
                                <div className={styles.preview} style={{ background }}>
                                    <div className={styles.previewText}>
                                        <Text className={styles.previewTitle}>{poll.data.Title}</Text>
                                        <Text className={styles.previewLabel}>Так участники увидят опрос на своих устройствах</Text>
                                    </div>
                                    {state.logoUrl && (
                                        <img
                                            src={state.logoUrl}
                                            alt="Логотип"
                                            className={styles.logo}
                                            style={{ ...LOGO_STYLE[state.logoPosition] ?? LOGO_STYLE[LOGO_POSITION_TOP_LEFT], width: state.logoSize }}
                                        />
                                    )}
                                    {state.qrVisible && qrPreview && (
                                        <img src={qrPreview} alt="QR-код" className={styles.qr} />
                                    )}
                                </div>
                            </Section>
                        </div>

                        <div className={styles.controls}>
                            <Section title="Фон">
                                <div className={styles.group}>
                                    <RadioGroup
                                        layout="horizontal"
                                        value={String(state.bgType)}
                                        onChange={(_, data) => patch({ bgType: Number(data.value) })}
                                        aria-label="Тип фона"
                                    >
                                        <Radio value={String(BG_SOLID)} label="Сплошной цвет" />
                                        <Radio value={String(BG_GRADIENT)} label="Градиент" />
                                    </RadioGroup>
                                    {state.bgType === BG_SOLID ? (
                                        <ColorField label="Цвет фона" value={state.bgColor} onChange={(bgColor) => patch({ bgColor })} />
                                    ) : (
                                        <>
                                            <ColorField label="Начало градиента" value={state.gradientStart} onChange={(gradientStart) => patch({ gradientStart })} />
                                            <ColorField label="Конец градиента" value={state.gradientEnd} onChange={(gradientEnd) => patch({ gradientEnd })} />
                                        </>
                                    )}
                                </div>
                            </Section>

                            <Section title="Логотип">
                                <div className={styles.group}>
                                    <div
                                        className={mergeClasses(styles.upload, dragOver && styles.uploadActive)}
                                        role="button"
                                        tabIndex={0}
                                        onClick={() => fileInput.current?.click()}
                                        onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') fileInput.current?.click(); }}
                                        onDragOver={(event) => { event.preventDefault(); setDragOver(true); }}
                                        onDragLeave={() => setDragOver(false)}
                                        onDrop={(event) => { event.preventDefault(); setDragOver(false); readLogo(event.dataTransfer.files?.[0]); }}
                                    >
                                        <ArrowUpload20Regular />
                                        <Text>{state.logoUrl ? 'Заменить изображение' : 'Выберите или перетащите файл'}</Text>
                                    </div>
                                    <input
                                        ref={fileInput}
                                        type="file"
                                        accept="image/*"
                                        hidden
                                        onChange={(event) => { readLogo(event.target.files?.[0]); event.target.value = ''; }}
                                    />
                                    <Text className={styles.hint}>PNG, JPG, SVG или WebP до 1,5 МБ. Хранится вместе с опросом.</Text>

                                    {state.logoUrl && (
                                        <>
                                            <div className={styles.currentLogo}>
                                                <img src={state.logoUrl} alt="Текущий логотип" className={styles.thumb} />
                                                <Button appearance="subtle" size="small" icon={<Delete20Regular />} onClick={() => patch({ logoUrl: '' })}>
                                                    Убрать
                                                </Button>
                                            </div>
                                            <Label>Положение</Label>
                                            <div className={styles.positions} role="radiogroup" aria-label="Положение логотипа">
                                                {LOGO_POSITION_OPTIONS.map((position) => (
                                                    <Button
                                                        key={position.code}
                                                        role="radio"
                                                        aria-checked={state.logoPosition === position.code}
                                                        appearance={state.logoPosition === position.code ? 'primary' : 'secondary'}
                                                        className={styles.positionButton}
                                                        onClick={() => patch({ logoPosition: position.code })}
                                                    >
                                                        {position.label}
                                                    </Button>
                                                ))}
                                            </div>
                                            <div className={styles.sliderHead}>
                                                <Label htmlFor="logo-size">Размер</Label>
                                                <Text className={styles.hint}>{state.logoSize} px</Text>
                                            </div>
                                            <Slider id="logo-size" min={40} max={240} step={4} value={state.logoSize} onChange={(_, data) => patch({ logoSize: data.value })} />
                                        </>
                                    )}
                                </div>
                            </Section>

                            <Section title="QR-код на экране">
                                <div className={styles.group}>
                                    <Switch
                                        label="Показывать QR-код со ссылкой на опрос"
                                        checked={state.qrVisible}
                                        onChange={(_, data) => patch({ qrVisible: data.checked })}
                                    />
                                    {state.qrVisible && !surveyUrl && (
                                        <Text style={{ color: tokens.colorStatusDangerForeground1 }}>
                                            QR-код не появится: у опроса не назначен slug.
                                        </Text>
                                    )}
                                    {state.qrVisible && (
                                        <>
                                            <ColorField label="Цвет QR-кода" value={state.qrForeground} onChange={(qrForeground) => patch({ qrForeground })} />
                                            <ColorField label="Фон QR-кода" value={state.qrBackground} onChange={(qrBackground) => patch({ qrBackground })} />
                                        </>
                                    )}
                                </div>
                            </Section>
                        </div>
                    </div>

                    <FormFooter
                        start={(
                            <Button appearance="subtle" icon={<Dismiss20Regular />} disabled={saving} onClick={() => navigate(detailsTo)}>
                                Отмена
                            </Button>
                        )}
                        end={(
                            <Button appearance="primary" icon={saving ? <Spinner size="tiny" /> : <Save20Regular />} disabled={saving} onClick={() => { void save(); }}>
                                {saving ? 'Сохраняем' : 'Сохранить'}
                            </Button>
                        )}
                    />
                </>
            )}
        </>
    );
};

export default PollBrandingPage;
